import React, { memo, useState, useEffect, useRef, useMemo } from 'react';
import { Button, Typography, List } from 'antd';
import { UpOutlined, DownOutlined } from '@ant-design/icons';
import { tabToName, getConstantLayoutAll } from 'src/util/common';
import { useIntl } from "react-intl";
import StyledTooltip from 'src/components/StyledTooltip';
import ArrowRight from 'src/assets/svg/ArrowRight';
import FavoriteYes from "src/assets/svg/FavoriteYes";
import FavoriteNo from "src/assets/svg/FavoriteNo";
import IntlMessages from 'src/util/IntlMessages';

const { Text } = Typography;

const Favorites = ({ dataSource, onUpdateFavorites, onClose }) => {
  const intl = useIntl();

  const sortByAddedKey = useMemo(() => intl.formatMessage({ id: 'common.sortByAdded' }), [intl]);
  const sortByNameKey = useMemo(() => intl.formatMessage({ id: 'common.sortByName' }), [intl]);
  const homeAll = useMemo(() => intl.formatMessage({ id: 'home.all' }), [intl]);



  const [sortKey, setSortKey] = useState(sortByNameKey);
  const [sortDirections, setSortDirections] = useState({
    [sortByAddedKey]: 'asc',
    [sortByNameKey]: 'desc',
  });

  const [showTooltip, setShowTooltip] = useState(false);


  const MANAGE_TABS = useMemo(
    () => [
      {
        key: 'overview',
        name: intl.formatMessage({ id: 'home.overviewFavorites' }),
      },
      {
        key: 'edit',
        name: intl.formatMessage({ id: 'home.editFavorites' }),
      },
    ],
    [intl],
  );


  const [activeTab, setActiveTab] = useState(MANAGE_TABS[0]);

  const ellipsisRef = useRef();

  const [localFavorites, setLocalFavorites] = useState([])

  const [sortedFavorites, setSortedFavorites] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);

  const ellipsisStyle = {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    lineHeight: "40px",
    position: "relative",
    width: "unset",
    maxWidth: 160,
  };
  const handleSort = (key) => {
    setSortKey(key);
    if (sortKey === key) {
      // 현재 정렬 방향 반전
      setSortDirections(prev => ({
        ...prev,
        [key]: prev[key] === 'asc' ? 'desc' : 'asc'
      }));
    }
  };

  useEffect(() => {
    if (dataSource.favorites) {
      initLocalFavorites();
    }
  }, [dataSource.favorites])


  useEffect(() => {
    const newData = localFavorites.filter((item) => item.isFavorite);
    const direction = sortDirections[sortKey];

    const sorted = [...newData].sort((a, b) => {
      if (sortKey === sortByNameKey) {
        const aText = `${a.computedGroupName}${a.name || ''}${a.product || ''}`;
        const bText = `${b.computedGroupName}${b.name || ''}${b.product || ''}`;
        return direction === 'asc'
          ? aText.localeCompare(bText)
          : bText.localeCompare(aText);
      } else {
        const indexA = a.at || 0;
        const indexB = b.at || 0;
        if (direction === 'asc') {
          return indexB - indexA;
        } else {
          return indexA - indexB;
        }
      }
    });

    setSortedFavorites(sorted);

    // console.log('DEBUG-002', JSON.stringify(dataSource.activeAgents, null, 2));
    // console.log('DEBUG-003', JSON.stringify(dataSource.agents, null, 2));

    const allProducts = [...localFavorites];
    if (dataSource.activeAgents && dataSource.agents) {
      const agentMaps = {};
      const favoriteMaps = {};
      dataSource.agents.forEach(agent => {
        agentMaps[agent.device_access_token] = agent;
      });
      localFavorites.forEach(favorite => {
        favoriteMaps[`${favorite.device_access_token}_${favorite.product}`] =
          favorite;
      });

      // console.log('DEBUG-005', JSON.stringify(favoriteMaps, null, 2));

      Object.keys(dataSource.activeAgents).forEach(key => {
        const agent = dataSource.activeAgents[key];
        dataSource.activeAgents[key]?.apps.forEach(app => {
          if (
            !favoriteMaps.hasOwnProperty(
              `${agent.device_access_token}_${app.title}`,
            )
          ) {
            // console.log('DEBUG-007', JSON.stringify(app, null, 2));
            if (agentMaps.hasOwnProperty(agent.device_access_token)) {
              // console.log("DEBUG-008", JSON.stringify(agentMaps[agent.device_access_token], null, 2) )
              let groupName = homeAll;
              if (agentMaps[agent.device_access_token].groups) {
                const filtered = agentMaps[
                  agent.device_access_token
                ].groups.filter(g => g.name !== getConstantLayoutAll());
                if (filtered.length > 0) {
                  groupName = filtered[0].name;
                }
              }
              allProducts.push({
                product: app.title,
                device_access_token: agent.device_access_token,
                name: agentMaps[agent.device_access_token].custom_name,
                group_name: groupName,
                isFavorite: false,
              });
            }
          }
        });
      });

      allProducts.sort((a, b) => {
        if (sortKey === sortByAddedKey) {
          // Assuming original favoriteList order reflects "added" order.
          // Uses original indices to sort. More robust: sort by an 'id' or 'timestamp' if available.
          const indexA = a.at || 0;
          const indexB = b.at || 0;
          if (direction === 'asc') {
            return indexB - indexA;
          } else {
            return indexA - indexB;
          }
        } else if (sortKey === sortByNameKey) {
          // Combine fields for name sorting as per the original TODO comment
          // Ensure to handle potential null/undefined values gracefully
          const valA = `${a.group_name || ''}${a.name || ''}${a.product || ''}`
            .trim()
            .toLowerCase();
          const valB = `${b.group_name || ''}${b.name || ''}${b.product || ''}`
            .trim()
            .toLowerCase();


          if (direction === 'asc') {
            return valB.localeCompare(valA);
          } else {
            return valA.localeCompare(valB);
          }
        }
        return 0; // Default: no change in order
      });

      // console.log('DEBUG-004', JSON.stringify(allProducts, null, 2));
    }
    setFilteredProducts(allProducts);
  }, [dataSource, localFavorites, sortKey, sortDirections, intl, activeTab, homeAll]);


    useEffect(() =>{
      // console.log("ellipsisRef.current", ellipsisRef.current)
      // console.log(" ellipsisRef.scrollWidth , ellipsisRef.clientWidth", ellipsisRef.current?.scrollWidth , ellipsisRef.current?.clientWidth)
      if (ellipsisRef.current) {
        if (ellipsisRef.current.scrollWidth > ellipsisRef.current?.clientWidth ) {
          setShowTooltip(true)
        }
        else {
          setShowTooltip(false)
        }
      }
    }, [ellipsisRef, dataSource])

  const ArrowRightComponent = memo(() => (
    <div style={{
      display: "inline-flex", alignItems: "center"
    }}>
      <ArrowRight />
    </div>))

  const handleFavorite = (item) => {
    if (activeTab.key === 'overview') return;

    const matched = localFavorites.find(
      d =>
        d.device_access_token ===
        item.device_access_token &&
        d.product === item.product,
    );

    const temp = [...localFavorites];
    if (matched) {
      temp.splice(temp.indexOf(matched), 1);
    } else {
      temp.push({ ...item, isFavorite: true, at: new Date().getTime() });
    }
    setLocalFavorites(temp)
    // onUpdateFavorites(favorites);
  }

  const initLocalFavorites = () => {
    setLocalFavorites(dataSource.favorites?.map((item) => {
      // console.log("DEBUG-001", JSON.stringify(item, null, 2));
      return {
        ...item,
        group_name: tabToName(item.group_name, homeAll),
        isFavorite: true
      }
    }))
  }

  const handleCancel = () => {
    // FIXED - 20250820: 이게 더 맞는 거 같지만 갑님의 요청으로...
    // initLocalFavorites();
    onClose?.();
  }

  const handleApply = () => {
    onUpdateFavorites(localFavorites.filter((item) => item.isFavorite))
  }

  return (
    <div>
      {/* 탭 UI */}
      <div style={{ display: 'flex' }}>
        {MANAGE_TABS.map((tab) => (
          <div
            key={tab.key}
            onClick={() => setActiveTab(tab)}
            style={{
              flex: 1,
              display: 'flex', // flex 사용
              alignItems: 'center', // 수직 가운데 정렬
              justifyContent: 'center', // 수평 가운데 정렬
              cursor: 'pointer',
              fontWeight: activeTab.key === tab.key ? 'bold' : 'normal',
              color: activeTab.key === tab.key ? '#df043a' : '#7E7E7E',
              borderTop: activeTab.key === tab.key ? '3px solid' : "none",
              borderColor: activeTab.key === tab.key ? '#df043a' : 'transparent',
              background: activeTab.key === tab.key ? '#fff' : '#DCDCDC',
              height: 38
            }}
          >
            {tab.name}
          </div>
        ))}
      </div>
      <div style={{ height: 350, overflow: 'hidden'}}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 26px 20px 20px'
            // borderBottom: '1px solid #eee',
          }}
        >
          <div style={{ display: 'flex' }}>
            {[sortByNameKey].map((key) => (
              <Button
                key={key}
                onClick={() => handleSort(key)}
                style={{
                  minWidth: 104,
                  height: 31,
                  // marginRight: 8,
                  borderRadius: 0,
                  borderColor: sortKey === key ? '#df043a' : '#B9B8B9',
                  color: sortKey === key ? '#df043a' : '#181818',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontSize: 16,
                }}
                size="small"
              >
                {key}{' '}
                {
                  sortDirections[key] === 'asc' ? (
                    <UpOutlined style={{ fontSize: 10 }} />
                  ) : (
                    <DownOutlined style={{ fontSize: 10 }} />
                  )
                }
              </Button>
            ))}
          </div>
          <Text type="secondary" style={{ color: '#181818', fontSize: 14 }}><IntlMessages id="home.totalCount" /> <span style={{ color: '#df043a' }}>{activeTab.key === 'overview' ? sortedFavorites.length : filteredProducts.length}</span></Text>
        </div>

        <div style={{ maxHeight: 260, overflowY: 'auto' }}>
          <List
            dataSource={activeTab.key === 'overview' ? sortedFavorites : filteredProducts}
            renderItem={(item, index) => (
              <List.Item style={{
                borderBlock: 'none',
                padding: 0,
                width: "100%",
                height: 48,
                borderBottom: '1px solid #DCDCDC',
                backgroundColor: index%2 === 1? '#f9f9f9': '#fff'  }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: "100%", justifyContent: "space-between", padding: '0 22px 0 20px'}}>

                  <StyledTooltip content={showTooltip ? (item.group_name ? (item.group_name + " > ") : "") + item.name + " > " + item.product : ""} placement="bottom" trigger="hover" >
                    <div style={{ overflow: "hidden" }}>
                      <div
                        style={{ display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }} ref={ellipsisRef}>
                        {/* <div style={{lineHeight: "47px"}}> */}
                        <span style={ellipsisStyle}>{item.group_name}</span>
                        {item.group_name && <ArrowRightComponent />}
                        {/* <span style={{color: "#B9B8B9"}}>{" > "}</span> */}
                        <span style={ellipsisStyle}>{item.name}</span>
                        <ArrowRightComponent />
                        <span>{item.product}</span>
                        {/* </div> */}
                      </div>
                    </div>
                  </StyledTooltip>
                  {item.isFavorite ? <FavoriteYes onClick={() => handleFavorite(item)} style={{ cursor: activeTab.key === 'overview' ? "" : "pointer" }} /> :
                    <FavoriteNo onClick={() => handleFavorite(item)} style={{ cursor: activeTab.key === 'overview' ? "" : "pointer" }} />}
                </div>
              </List.Item>
            )}
          />
        </div>
      </div>
      <div style={{ padding: '0 20px 8px 20px', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <Button className="gx-mb-0" style={{
          borderRadius: 4,
        }}
          disabled={activeTab.key === 'overview'}
          onClick={handleCancel}
        ><IntlMessages id="vlink.common.cancel" /></Button>
        <Button type={activeTab.key === 'overview' ? "" : "primary"} className="gx-mb-0" style={{
          borderRadius: 4,
          backgroundColor: activeTab.key === 'overview' ? "#0000000a" : "#DF043A",
        }}
          disabled={activeTab.key === 'overview'}
          onClick={handleApply}
        ><IntlMessages id="vlink.common.apply" /></Button>
      </div>
    </div>
  );
};

export default Favorites;
